import { useEffect, useState, useCallback } from "react";
import { Combobox, Listbox, Icon, LegacyStack, Tag } from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import _ from "lodash";
import { fetchSignatureTypes } from "services/signature.service";

interface Option {
  label: string;
  value: string;
}

interface SignatureSelectProps {
  value?: string[];
  onChange: (value: string[]) => void;
  isRequired?: boolean;
  isDisable?: boolean;
  label?: string;
  readOnly?: boolean;
}

const SignatureSelect: React.FC<SignatureSelectProps> = ({
  value = [],
  onChange,
  isRequired = false,
  label = "select_signature_types",
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const { companyId } = useParams<{ companyId: string }>();

  const fetchAndSetInitialValue = useCallback(
    async (ids: string | string[]) => {
      const idArray = Array.isArray(ids) ? ids : ids ? [ids] : [];
      if (!idArray.length) return;
      setLoading(true);
      try {
        const signatureTypes = await fetchSignatureTypes(companyId);

        const selected_ = signatureTypes.filter((signatuerTypesEntity: any) => {
          if (idArray.includes(signatuerTypesEntity?.id)) {
            return true;
          }
        });

        const selected = selected_.map((signatuerTypesEntity: any) => ({
          value: signatuerTypesEntity.id,
          label: signatuerTypesEntity.label,
        }));

        setSelectedOptions(selected);
      } finally {
        setLoading(false);
      }
    },
    [companyId]
  );
  useEffect(() => {
    if (value !== undefined) {
      fetchAndSetInitialValue(value);
    }
  }, [JSON.stringify(value), fetchAndSetInitialValue]);

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSignatureTypes(companyId);
      const dataCollection = _.map(data, (row) => ({
        label: row.title || row.label,
        value: row.id,
      }));
      setOptions(dataCollection);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      fetchOptions();
    }
  }, [companyId, fetchOptions]);

  const handleInputChange = (input: string) => {
    setInputValue(input);
  };

  const handleSelection = (selected: string) => {
    if (selectedOptions.find((option: Option) => option.value === selected)) {
      setSelectedOptions(
        selectedOptions.filter((option: Option) => option.value !== selected)
      );
    } else {
      const findSelectedOption = options.find(
        (option) => option.value === selected
      );
      if (findSelectedOption)
        setSelectedOptions((prev: Option[]) => [...prev, findSelectedOption]);
      onChange([
        ...selectedOptions.map((option: Option) => option.value),
        selected,
      ]);
    }
  };

  const handleTagRemove = (tagToRemove: Option) => {
    if (readOnly) return;
    const updatedOptions = selectedOptions.filter(
      (option) => option.value !== tagToRemove.value
    );
    setSelectedOptions(updatedOptions);
    onChange(updatedOptions.map((option) => option.value));
  };

  const optionsMarkup = options.map((option) => (
    <Listbox.Option
      disabled={readOnly}
      key={option.value}
      value={option.value}
      selected={
        selectedOptions.filter(
          (entity: Option) => entity.value === option.value
        ).length > 0
      }
      accessibilityLabel={option.label}
    >
      {option.label}
    </Listbox.Option>
  ));

  const verticalContentMarkup = selectedOptions.map((option) => (
    <Tag key={option.value} onRemove={() => handleTagRemove(option)}>
      {option.label}
    </Tag>
  ));

  return (
    <div className="signature-select">
      <Combobox
        allowMultiple
        activator={
          <Combobox.TextField
            readOnly={readOnly}
            prefix={<Icon source={SearchIcon as any} />}
            onChange={handleInputChange}
            label={t(label)}
            value={inputValue}
            autoComplete="off"
            requiredIndicator={isRequired}
            error={
              isRequired && selectedOptions.length === 0
                ? t("field_is_required")
                : ""
            }
            verticalContent={
              <LegacyStack spacing="extraTight" alignment="center">
                {verticalContentMarkup}
              </LegacyStack>
            }
          />
        }
      >
        <Listbox onSelect={handleSelection}>
          {loading ? (
            <Listbox.Loading accessibilityLabel="Loading" />
          ) : (
            optionsMarkup
          )}
        </Listbox>
      </Combobox>
    </div>
  );
};

export default SignatureSelect;
